import {
  ANALYTICS_DEBUG_EVENT,
  ANALYTICS_DEBUG_STORAGE_KEY,
  type AnalyticsDebugEntry,
  readAnalyticsDebugEntries,
} from "@/components/analytics/debug-events";
import { ANALYTICS_EVENT_CATALOG } from "@/components/analytics/event-catalog";
import { Button } from "@/components/ui/button";
import { Activity, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";

export function AnalyticsDebugPanel() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<AnalyticsDebugEntry[]>([]);

  useEffect(() => {
    setEntries(readAnalyticsDebugEntries());
    const update = () => setEntries(readAnalyticsDebugEntries());
    window.addEventListener(ANALYTICS_DEBUG_EVENT, update);
    return () => window.removeEventListener(ANALYTICS_DEBUG_EVENT, update);
  }, []);

  if (!import.meta.env.DEV) return null;

  const clear = () => {
    window.sessionStorage.removeItem(ANALYTICS_DEBUG_STORAGE_KEY);
    setEntries([]);
  };

  const observedEvents = new Set(entries.map((entry) => entry.name));
  const observedCount = ANALYTICS_EVENT_CATALOG.filter((event) =>
    observedEvents.has(event.name),
  ).length;

  return (
    <div className="fixed right-3 bottom-3 z-[100]">
      {open && (
        <section className="mb-2 flex max-h-[70vh] w-[min(92vw,430px)] flex-col overflow-hidden rounded-2xl border bg-background/98 shadow-2xl backdrop-blur">
          <header className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <h2 className="font-semibold text-sm">Analytics Debug</h2>
              <p className="text-muted-foreground text-xs">
                Yalnızca local geliştirme ortamı
              </p>
            </div>
            <div className="flex gap-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={clear}
                title="Temizle"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </header>
          <div className="overflow-y-auto p-3">
            <div className="mb-3 rounded-xl border bg-muted/30 p-3">
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="font-semibold">Oturum test kapsamı</span>
                <span className="text-muted-foreground">
                  {observedCount}/{ANALYTICS_EVENT_CATALOG.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ANALYTICS_EVENT_CATALOG.map((event) => {
                  const observed = observedEvents.has(event.name);
                  return (
                    <span
                      key={event.name}
                      title={`${event.label} · ${event.tier}`}
                      className={
                        observed
                          ? "rounded-full bg-emerald-500/15 px-2 py-1 text-[10px] text-emerald-700 dark:text-emerald-400"
                          : "rounded-full bg-muted px-2 py-1 text-[10px] text-muted-foreground"
                      }
                    >
                      {observed ? "✓" : "○"} {event.name}
                    </span>
                  );
                })}
              </div>
            </div>
            {entries.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground text-sm">
                Henüz dönüşüm olayı tetiklenmedi.
              </p>
            ) : (
              <div className="space-y-2">
                {entries.map((entry) => (
                  <article
                    key={entry.id}
                    className="rounded-xl border p-3 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <code className="font-semibold text-primary">
                        {entry.name}
                      </code>
                      <span
                        className={
                          entry.status === "sent"
                            ? "text-emerald-600"
                            : "text-amber-600"
                        }
                      >
                        {entry.status === "sent" ? "İzinli" : "İzin engelledi"}
                      </span>
                    </div>
                    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-muted p-2 text-[11px]">
                      {JSON.stringify(entry.params, null, 2)}
                    </pre>
                    <time className="mt-2 block text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleTimeString("tr-TR")}
                    </time>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <Button
        className="gap-2 rounded-full shadow-lg"
        onClick={() => setOpen((value) => !value)}
      >
        <Activity className="h-4 w-4" />
        Analytics {entries.length > 0 ? `(${entries.length})` : ""}
      </Button>
    </div>
  );
}
